<template lang="html">
    <div v-if="modelValue">
        <v-dialog
            v-model="showModal"
            :content-class="getContentClass"
            :fullscreen="width === 'full'"
            :max-width="getWidth(width)"
            :persistent="persistent"
            :transition="getTransition"
            :z-index="zIndex"
            scrollable
        >
            <v-card
                :class="getCardClass"
                class="Modal"
            >
                <modal-header
                    :title
                    :isClosable
                    @closeModal="closeModal()"
                />
                <!-- eslint-disable -->
                <v-card-subtitle
                    v-if="subtitle"
                    class="Modal-subtitle"
                    v-html="$sanitize(subtitle)"
                />
                <!-- eslint-enable -->
                <v-card-text
                    v-if="$slots.default"
                    class="Modal-body"
                >
                    <slot />
                </v-card-text>
                <v-card-actions
                    v-if="$slots.actions"
                    class="Modal-actions"
                >
                    <slot name="actions" />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, onMounted, onBeforeUnmount } from 'vue'
import ModalHeader from '@/components/subComponents/modal/ModalHeader.vue'

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    isClosable: {
        type: Boolean,
        default: true
    },
    isRounded: {
        type: Boolean,
        default: true
    },
    persistent: {
        type: Boolean,
        default: true
    },
    subtitle: {
        default: '',
        type: String
    },
    title: {
        default: '',
        type: String
    },
    width: {
        type: String,
        default: 'medium',
        validator: function (value) {
            return ['small', 'medium', 'large', 'full'].includes(value)
        }
    },
    zIndex: {
        type: Number,
        default: 2000
    }
})

const emit = defineEmits(['closeModal', 'update:modelValue'])

const { proxy } = getCurrentInstance()

const getCardClass = computed(() => {
    if (props.width === 'full') {
        return 'rounded-0'
    }

    if (props.isRounded) {
        if (proxy.$vuetify.display.xs) {
            return 'rounded-t-xl rounded-b-0'
        }

        return 'rounded-xl'
    }

    return ''
})
const getContentClass = computed(() => {
    if (props.width === 'full') {
        return 'rounded-0'
    }

    if (proxy.$vuetify.display.xs) {
        const classList = 'v-dialog--drawer align-self-end ma-0 w-100'

        if (props.width === 'full') {
            return `${classList} rounded-0`
        }

        return `${classList}${props.isRounded ? ' rounded-t-xl' : ''} rounded-b-0`
    }

    return ''
})
const getTransition = computed(() => {
    if (props.width === 'full') {
        return 'scale-transition'
    }

    if (proxy.$vuetify.display.xs) {
        return 'dialog-bottom-transition'
    }

    return 'scale-transition'
})

const modalHeight = ref('100%')

const showModal = computed({
    get: () => props.modelValue,
    set: (newValue) => {
        emit('update:modelValue', newValue)
    }
})

function closeModal () {
    emit('update:modelValue', false)
    emit('closeModal')
}

function getWidth (width) {
    switch (width) {
        case 'small':
            return '500px'
        case 'medium':
            return '800px'
        case 'large':
            return '1200px'
        case 'full':
            return '100%'
        default:
            return '800px'
    }
}

const updateModalHeight = () => {
    modalHeight.value = `${window.innerHeight}px`
}

onMounted(() => {
    window.addEventListener('resize', updateModalHeight)
    updateModalHeight()
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateModalHeight)
})
</script>

<style lang="scss">
@import '@/assets/scss/global';

.Modal {
    &-subtitle {
        overflow: visible !important;
        padding: 0 $s4 !important;
        font-size: $font-medium !important;
        text-overflow: unset !important;
        white-space: normal !important;
        opacity: .9 !important;
    }

    &-body {
        padding: $s4 !important;
        padding-top: $s8 !important;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
    }

    &-actions {
        padding: $s4 !important;
    }
}
</style>
